import type { Simplify, SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import type { PersonInvolvement } from "./involvement.js";
import type { OrganisationId } from "./organisation.js";

/** Who a professional contributor is to the person. */
export type ProfessionalRelation =
  | "teacher"
  | "school_staff"
  | "gp"
  | "health_professional"
  | "youth_justice_worker"
  | "legal_representative"
  | "advocate"
  | "independent_reviewing_officer"
  | "other";

const ProfessionalRelationSchema = z.enum([
  "teacher",
  "school_staff",
  "gp",
  "health_professional",
  "youth_justice_worker",
  "legal_representative",
  "advocate",
  "independent_reviewing_officer",
  "other",
]) satisfies ZodType<ProfessionalRelation & SnakeCase<ProfessionalRelation>>;

/** The professional group's relations, in the order a form offers them. */
export const professionalRelations = ProfessionalRelationSchema.options;

type FamilyRelation = "mother" | "father" | "step_parent" | "sibling" | "other";

const FamilyRelationSchema = z.enum([
  "mother",
  "father",
  "step_parent",
  "sibling",
  "other",
]) satisfies ZodType<FamilyRelation & SnakeCase<FamilyRelation>>;

type CarerRelation = "kinship_carer" | "foster_carer" | "other";

const CarerRelationSchema = z.enum([
  "kinship_carer",
  "foster_carer",
  "other",
]) satisfies ZodType<CarerRelation & SnakeCase<CarerRelation>>;

// Long enough for any honest description or title, short enough that neither
// becomes prose about the person.
const standingTextMaxLength = 200;

// Blank is no words at all, so it is held as none and judged as none.
const RelationDescriptionSchema = z
  .string()
  .trim()
  .max(standingTextMaxLength, "standing.relationDescriptionTooLong")
  .transform((description) => (description === "" ? null : description))
  .nullable();

const needsDescription = (relation: {
  relationGroup: string;
  relation: string;
}): boolean =>
  relation.relationGroup === "other" || relation.relation === "other";

// Closed in the schema rather than the column, as decision
// `2026-08-28-the-role-set-is-closed-in-code-open-in-the-column` closes the
// role set, so widening it is a line here.
export const ContributorRelationSchema = z
  .discriminatedUnion("relationGroup", [
    z.object({
      relationGroup: z.literal("professional"),
      relation: ProfessionalRelationSchema,
      relationDescription: RelationDescriptionSchema,
    }),
    z.object({
      relationGroup: z.literal("family"),
      relation: FamilyRelationSchema,
      relationDescription: RelationDescriptionSchema,
    }),
    z.object({
      relationGroup: z.literal("carer"),
      relation: CarerRelationSchema,
      relationDescription: RelationDescriptionSchema,
    }),
    z.object({
      relationGroup: z.literal("self"),
      relation: z.literal("self"),
      relationDescription: RelationDescriptionSchema,
    }),
    z.object({
      relationGroup: z.literal("other"),
      relation: z.literal("other"),
      relationDescription: RelationDescriptionSchema,
    }),
  ])
  .superRefine((relation, context) => {
    if (needsDescription(relation) && relation.relationDescription === null) {
      context.addIssue({
        code: "custom",
        message: "standing.relationDescriptionRequired",
        path: ["relationDescription"],
      });
    }

    if (!needsDescription(relation) && relation.relationDescription !== null) {
      context.addIssue({
        code: "custom",
        message: "standing.relationDescriptionUnexpected",
        path: ["relationDescription"],
      });
    }
  })
  .brand<"haven-common:ContributorRelation">() satisfies ZodType<ContributorRelationUnbranded>;

/**
 * Who a contributor is to the person (`docs/glossary/relation.md`): a
 * group, a relation within it, and their own words only where either is
 * `other`. Product owns the words of the set.
 */
export type ContributorRelation = z.infer<typeof ContributorRelationSchema>;

/**
 * A relation on the wire. Written out because tsoa follows a type into the API
 * contract but cannot follow `z.infer`.
 */
export type ContributorRelationUnbranded =
  | {
      relationGroup: "professional";
      relation: ProfessionalRelation;
      relationDescription: string | null;
    }
  | {
      relationGroup: "family";
      relation: FamilyRelation;
      relationDescription: string | null;
    }
  | {
      relationGroup: "carer";
      relation: CarerRelation;
      relationDescription: string | null;
    }
  | {
      relationGroup: "self";
      relation: "self";
      relationDescription: string | null;
    }
  | {
      relationGroup: "other";
      relation: "other";
      relationDescription: string | null;
    };

export const JobTitleSchema = z
  .string()
  .trim()
  .min(1, "standing.jobTitleRequired")
  .max(standingTextMaxLength, "standing.jobTitleTooLong")
  .brand<"haven-common:JobTitle">() satisfies ZodType<JobTitleUnbranded>;

/**
 * A contributor's job, in their own words. Free text about a named person, so
 * it stays out of every analytics model, unlike the relation beside it.
 */
export type JobTitle = z.infer<typeof JobTitleSchema>;

export type JobTitleUnbranded = string;

/**
 * One of somebody's involvements with a person: the organisation they
 * hold it on behalf of, none for a guest, and the relation it records, if any.
 */
export type InvolvementWithRelation = Simplify<
  Pick<PersonInvolvement, "kind" | "startedAt" | "endedAt" | "expiresAt"> & {
    onBehalfOfOrganisationId: OrganisationId | null;
    relation: ContributorRelation | null;
  }
>;
